import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import TranslationsInput from 'components/forms/inputs/translations/TranslationsInput';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Controller } from 'react-hook-form';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { JobPositionFormState } from './useJobPositionForm';

interface Props {
    form: JobPositionFormState;
    /**
     * Shows the external id without letting it change: the server takes it on create only. On the record page it
     * is not drawn at all, the card's own row shows it.
     */
    isExternalIdReadOnly?: boolean;
}

/** The name, external id and type; the last two behind their flags. */
export const JobPositionFields = ({ form, isExternalIdReadOnly = false }: Props): JSX.Element => {
    const { control, setValue } = form.methods;
    const inSection = useInRecordSection();
    return (
        <>
            <TextInput control={control} label="name" placeholder="forms.enter-name" name="name" required />
            {form.isJobPositionExternalIdEnabled && !(inSection && isExternalIdReadOnly) ? (
                <TextInput
                    control={control}
                    label="job-positions.external-id"
                    placeholder="forms.enter-external-id"
                    name="externalId"
                    required={!isExternalIdReadOnly}
                    readonly={isExternalIdReadOnly}
                />
            ) : null}
            {form.isJobPositionTypeEnabled ? (
                <SelectInput
                    setValue={setValue}
                    control={control}
                    label="job-positions.position-type"
                    placeholder="forms.select-position-type"
                    name="positionTypeCode"
                    options={
                        (getValidOptionsFromClassifier(form.classifiers['JOB_POSITION_TYPE']) as {
                            label: string;
                            value: string;
                        }[]) ?? []
                    }
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    required
                />
            ) : null}
        </>
    );
};

/** The position's name in each language. */
export const JobPositionTranslationsFields = ({ form }: { form: JobPositionFormState }): JSX.Element => {
    const { control, getValues } = form.methods;
    return (
        <Controller
            control={control}
            name="entityTexts"
            render={({ field }) => (
                <TranslationsInput
                    labelId="job-positions.position-name-translations"
                    classifiers={form.classifiers}
                    control={control}
                    entityTexts={field.value ?? []}
                    getValues={getValues}
                />
            )}
        />
    );
};
